if (!customElements.get('type-tester')) {
  customElements.define(
    'type-tester',
    class TypeTester extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        const configEl = this.querySelector('[data-type-tester-config]');
        if (!configEl) return;

        try {
          this.config = JSON.parse(configEl.textContent);
        } catch (e) {
          console.error('Type tester: invalid config', e);
          return;
        }

        this.styles = (this.config.styles || []).map((style) => ({
          ...style,
          axes: TypeTester.parseAxes(style.axes),
        }));
        this.features = TypeTester.parseFeatures(this.config.features);
        if (!this.styles.length) return;

        this.initialized = true;
        this.specimen = this.querySelector('[data-specimen]');
        this.controls = this.querySelector('[data-controls]');
        this.uid = Math.random().toString(36).slice(2, 8);

        this.buildControls();
        this.reset();
        this.preloadFonts();

        this.querySelector('[data-reset]').addEventListener('click', () => this.reset());
        this.querySelector('[data-buy]')?.addEventListener('click', (event) => {
          const target = document.querySelector('[id^="ProductInfo-"]');
          if (!target) return;
          event.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        this.specimen.addEventListener('paste', (event) => this.onPaste(event));
        this.specimen.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.specimen.blur();
        });
      }

      // "wght | Weight | 35 | 200 | 110 | Hairline=35, Black=200" → axis objects
      static parseAxes(source) {
        if (!source) return [];
        return source
          .split(/\r?\n/)
          .map((line) => line.split('|').map((part) => part.trim()))
          .filter((parts) => parts.length >= 5 && parts[0])
          .map(([tag, label, min, max, def, stops = '']) => ({
            tag,
            label: label || tag,
            min: parseFloat(min),
            max: parseFloat(max),
            default: parseFloat(def),
            stops: stops
              .split(',')
              .map((stop) => stop.split('=').map((part) => part.trim()))
              .filter(([name, value]) => name && value !== undefined && !isNaN(parseFloat(value)))
              .map(([name, value]) => ({ name, value: parseFloat(value) })),
          }))
          .filter((axis) => [axis.min, axis.max, axis.default].every((n) => !isNaN(n)) && axis.max > axis.min);
      }

      // Fetch every style up front so switching styles never flashes the fallback font
      preloadFonts() {
        if (!document.fonts?.load) return;
        this.styles.forEach((style) => {
          const weight = style.axes.length ? 400 : style.weight;
          document.fonts.load(`${weight} 16px '${style.family}'`).catch(() => {});
        });
      }

      // "swsh | Swash | on" → { tag: 'swsh', label: 'Swash', default: true }
      static parseFeatures(source) {
        if (!source) return [];
        return source
          .split(/\r?\n/)
          .map((line) => line.split('|').map((part) => part.trim()))
          .filter(([tag]) => /^[a-zA-Z0-9]{4}$/.test(tag || ''))
          .map(([tag, label, state = '']) => ({
            tag,
            label: label || tag,
            default: /^(on|yes|true|1)$/i.test(state),
          }));
      }

      get isMobile() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      buildControls() {
        const { labels } = this.config;
        this.controls.innerHTML = '';

        // Style switch (only when there is more than one style)
        if (this.styles.length > 1) {
          const group = this.createGroup('type-tester__group--style');
          group.append(this.createLabel(labels.style));
          const list = document.createElement('div');
          list.className = 'type-tester__styles';
          list.setAttribute('role', 'radiogroup');
          list.setAttribute('aria-label', labels.style);
          this.styleButtons = this.styles.map((style, index) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'type-tester__style';
            button.textContent = style.label;
            button.setAttribute('role', 'radio');
            button.addEventListener('click', () => this.selectStyle(index));
            list.append(button);
            return button;
          });
          group.append(list);
          this.controls.append(group);
        }

        // Axis sliders, one set per variable style; hidden when another style is active
        this.axisGroups = this.styles.map((style) => {
          const wrapper = document.createElement('div');
          wrapper.className = 'type-tester__axes';

          if (!style.axes.length) {
            if (style.note) {
              const note = document.createElement('p');
              note.className = 'type-tester__note';
              note.textContent = style.note;
              wrapper.append(note);
            }
            return { wrapper, inputs: [] };
          }

          const inputs = style.axes.map((axis) => {
            const group = this.createGroup('type-tester__group--axis');
            const id = `tt-${this.uid}-${style.id}-${axis.tag}`;
            const header = document.createElement('div');
            header.className = 'type-tester__group-header';
            const label = this.createLabel(axis.label, id);
            const output = document.createElement('output');
            output.className = 'type-tester__value';
            output.setAttribute('for', id);
            header.append(label, output);

            const input = this.createRange(id, axis.min, axis.max, this.stepFor(axis));
            input.addEventListener('input', () => {
              this.axisValues[style.id][axis.tag] = parseFloat(input.value);
              this.render();
            });
            group.append(header, input);

            let stopButtons = [];
            if (axis.stops.length) {
              const stops = document.createElement('div');
              stops.className = 'type-tester__stops';
              stopButtons = axis.stops.map((stop) => {
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'type-tester__stop';
                button.textContent = stop.name;
                button.addEventListener('click', () => {
                  this.axisValues[style.id][axis.tag] = stop.value;
                  this.render();
                });
                stops.append(button);
                return { button, stop };
              });
              group.append(stops);
            }

            wrapper.append(group);
            return { axis, input, output, stopButtons };
          });

          if (style.note) {
            const note = document.createElement('p');
            note.className = 'type-tester__note';
            note.textContent = style.note;
            wrapper.append(note);
          }
          return { wrapper, inputs };
        });
        this.axisGroups.forEach(({ wrapper }) => this.controls.append(wrapper));

        // Size
        const { size } = this.config;
        const sizeGroup = this.createGroup('type-tester__group--size');
        const sizeId = `tt-${this.uid}-size`;
        const sizeHeader = document.createElement('div');
        sizeHeader.className = 'type-tester__group-header';
        this.sizeOutput = document.createElement('output');
        this.sizeOutput.className = 'type-tester__value';
        this.sizeOutput.setAttribute('for', sizeId);
        sizeHeader.append(this.createLabel(labels.size, sizeId), this.sizeOutput);
        this.sizeInput = this.createRange(sizeId, size.min, size.max, 1);
        this.sizeInput.addEventListener('input', () => {
          this.size = parseFloat(this.sizeInput.value);
          this.render();
        });
        sizeGroup.append(sizeHeader, this.sizeInput);
        this.controls.append(sizeGroup);

        // OpenType feature toggles
        this.featureButtons = [];
        if (this.features.length) {
          const group = this.createGroup('type-tester__group--features');
          group.append(this.createLabel(labels.features));
          const list = document.createElement('div');
          list.className = 'type-tester__features';
          this.featureButtons = this.features.map((feature) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'type-tester__feature';
            button.textContent = feature.label;
            button.title = feature.tag;
            button.addEventListener('click', () => {
              this.featureStates[feature.tag] = !this.featureStates[feature.tag];
              this.render();
            });
            list.append(button);
            return { button, feature };
          });
          group.append(list);
          this.controls.append(group);
        }
      }

      createGroup(modifier) {
        const group = document.createElement('div');
        group.className = `type-tester__group ${modifier}`;
        return group;
      }

      createLabel(text, htmlFor) {
        const label = document.createElement(htmlFor ? 'label' : 'span');
        label.className = 'type-tester__label';
        label.textContent = text;
        if (htmlFor) label.htmlFor = htmlFor;
        return label;
      }

      createRange(id, min, max, step) {
        const input = document.createElement('input');
        input.type = 'range';
        input.id = id;
        input.className = 'type-tester__range';
        input.min = min;
        input.max = max;
        input.step = step;
        return input;
      }

      stepFor(axis) {
        return axis.max - axis.min <= 10 ? 0.1 : 1;
      }

      selectStyle(index) {
        this.styleIndex = index;
        this.render();
      }

      reset() {
        const { size, text } = this.config;
        this.styleIndex = 0;
        this.featureStates = {};
        this.features.forEach((feature) => {
          this.featureStates[feature.tag] = feature.default;
        });
        this.axisValues = {};
        this.styles.forEach((style) => {
          this.axisValues[style.id] = {};
          style.axes.forEach((axis) => {
            this.axisValues[style.id][axis.tag] = axis.default;
          });
        });
        const defaultSize = this.isMobile && size.defaultMobile ? size.defaultMobile : size.default;
        this.size = Math.min(Math.max(defaultSize, size.min), size.max);
        this.specimen.textContent = text;
        this.render();
      }

      render() {
        const style = this.styles[this.styleIndex];
        const values = this.axisValues[style.id];
        const s = this.specimen.style;

        s.fontFamily = `'${style.family}', var(--font-body-family)`;
        s.fontSize = `${this.size}px`;

        s.fontFeatureSettings =
          this.features.map((feature) => `'${feature.tag}' ${this.featureStates[feature.tag] ? 1 : 0}`).join(', ') ||
          'normal';

        if (style.axes.length) {
          const settings = style.axes.map((axis) => `'${axis.tag}' ${values[axis.tag]}`);
          s.fontVariationSettings = settings.join(', ') || 'normal';
          // Keep font-weight in sync with wght so the browser never clamps or synthesizes bold
          s.fontWeight = values.wght !== undefined ? values.wght : 'normal';
        } else {
          s.fontVariationSettings = 'normal';
          s.fontWeight = style.weight;
        }

        this.styleButtons?.forEach((button, index) => {
          const active = index === this.styleIndex;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-checked', active);
        });

        this.featureButtons.forEach(({ button, feature }) => {
          const on = this.featureStates[feature.tag];
          button.classList.toggle('is-active', on);
          button.setAttribute('aria-pressed', on);
        });

        this.axisGroups.forEach(({ wrapper, inputs }, index) => {
          const visible = index === this.styleIndex && wrapper.childElementCount > 0;
          wrapper.hidden = !visible;
          if (!visible) return;
          inputs.forEach(({ axis, input, output, stopButtons }) => {
            const value = values[axis.tag];
            const stop = axis.stops.find((item) => item.value === value);
            input.value = value;
            output.textContent = stop ? `${stop.name} ${value}` : Math.round(value * 10) / 10;
            input.setAttribute('aria-valuetext', stop ? `${stop.name}, ${value}` : `${value}`);
            TypeTester.paintRange(input);
            stopButtons.forEach(({ button, stop: item }) => {
              button.classList.toggle('is-active', item.value === value);
            });
          });
        });

        this.sizeInput.value = this.size;
        this.sizeOutput.textContent = `${this.size}px`;
        TypeTester.paintRange(this.sizeInput);
      }

      // Fill the track up to the thumb (WebKit has no ::-webkit-slider-runnable-track progress)
      static paintRange(input) {
        const min = parseFloat(input.min);
        const max = parseFloat(input.max);
        const pct = ((parseFloat(input.value) - min) / (max - min)) * 100;
        input.style.setProperty('--tt-progress', `${pct}%`);
      }

      onPaste(event) {
        event.preventDefault();
        const text = (event.clipboardData || window.clipboardData).getData('text/plain');
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        selection.deleteFromDocument();
        const range = selection.getRangeAt(0);
        const node = document.createTextNode(text);
        range.insertNode(node);
        range.setStartAfter(node);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  );
}
